import { API, ApiResponse, handleApiResponse, handleApiError } from './config';

/**
 * Interfaz de billetera
 */
export interface Wallet {
  id: number | string;
  amount: number | string;
  /**
   * Tipo de wallet (viene como número del servidor):
   *   0 = Principal (balance operacional)
   *   1 = Comisión
   *   2 = Crédito
   */
  is_primary: number | boolean;
  debt?: number | string | null;
}

/**
 * Respuesta de billeteras
 */
export interface WalletsResponse {
  wallets: Wallet[];
  /** 0 = usa Principal por defecto, 1 = usa Crédito por defecto */
  walletSelected: number;
}

/**
 * Transacción de wallet
 */
export interface WalletTransaction {
  id: string;
  transaction_id: string;
  amount: string;
  date: string;
  type: number;
  actual_amount: string;
  process: number;
  reason: string;
}

/**
 * Ítem del historial de crédito
 * proccess: 0 = Crédito otorgado (ADD), 4 = Pago realizado (PAY)
 */
export interface CreditHistoryItem {
  xid: string;
  date: string;
  amount: string;
  proccess: number;
  actual_amount: string;
}

/**
 * Respuesta de movimientos de wallet
 */
export interface WalletMovementsResponse {
  data: WalletTransaction[];
  current_page: number;
  last_page: number;
}

/**
 * Servicio para billetera y operaciones relacionadas
 */
export const walletService = {
  /**
   * Obtener las billeteras del usuario
   */
  getWallets: async (): Promise<ApiResponse<WalletsResponse>> => {
    try {
      const response = await API.post('/get-wallets', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Historial de crédito (créditos otorgados y pagos realizados sobre la wallet de crédito)
   */
  getCreditHistory: async (page = 1): Promise<ApiResponse<{
    data: CreditHistoryItem[];
    current_page: number;
    last_page: number;
    current_debt: number | string;
  }>> => {
    try {
      const response = await API.post('/credit-history', { page });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  /**
   * Obtener los movimientos de una billetera específica
   */
  /**
   * Transferir saldo de wallet de comisiones a wallet principal
   * No recibe parámetros — transfiere el saldo completo automáticamente
   */
  transferWalletFunds: async (): Promise<ApiResponse<null>> => {
    try {
      const response = await API.post('/transfer-wallet-funds', {});
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },

  getWalletMovements: async (
    id: string,
    page?: number,
    filters?: { from_date?: string; to_date?: string; type?: number | string; process?: number | string }
  ): Promise<ApiResponse<WalletMovementsResponse>> => {
    try {
      const response = await API.post('/get-wallet-movements', {
        id,
        page: page || 1,
        per_page: 15,
        ...filters,
      });
      return handleApiResponse(response);
    } catch (error) {
      throw handleApiError(error);
    }
  },
};
